import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button, Icon, Select, theme } from "uikit";

import { Diagnostics } from "../../../../../services";
import { RANGE_OPTIONS, REFRESH_INTERVAL, RangeKey } from "../constants";
import { useSystemPerformance } from "../context";
import { buildOverviewReport } from "../utils/buildReport";

import CopyReportButton from "./CopyReportButton";

const Root = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
	min-width: 0;
	max-width: 100%;
`;

const Field = styled.label`
	display: flex;
	flex-direction: column;
	gap: 4px;

	font-family: Lato;
	font-size: 12px;
	color: ${theme.colors.secondary};
`;

const RefreshBlock = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 10px;
`;

const ProgressTrack = styled.div`
	width: 120px;
	height: 4px;
	border-radius: 2px;
	background: ${theme.colors.button_secondary};
	overflow: hidden;
	flex-shrink: 0;
`;

const ProgressFill = styled.div<{ $progress: number }>`
	height: 100%;
	width: ${({ $progress }) => `${Math.min(100, Math.max(0, $progress * 100))}%`};
	background: #03a9f4;
	transition: width 0.2s linear;
`;

const ProgressLabel = styled.span`
	font-family: Lato;
	font-size: 11px;
	line-height: 1;
	color: ${theme.colors.secondary};
	white-space: nowrap;
`;

type Props = {
	/** На вкладке инцидентов сервис не фильтрует список — селектор скрываем. */
	hideService?: boolean;
};

const Toolbar: React.FC<Props> = ({ hideService = false }) => {
	const { t } = useTranslation();
	const {
		services,
		service,
		setService,
		range,
		setRange,
		rangeMinutes,
		refreshToken,
		refresh,
	} = useSystemPerformance();
	const [refreshProgress, setRefreshProgress] = useState(0);

	const serviceOptions = useMemo(
		() =>
			services.map((item) => ({
				key: item.service,
				label: item.service,
				value: item.service,
			})),
		[services],
	);

	const rangeOptions = useMemo(
		() =>
			RANGE_OPTIONS.map((item) => ({
				key: item.key,
				label: t(`settings.systemPerformance.ranges.${item.key}`),
				value: item.key,
			})),
		[t],
	);

	const buildReport = useCallback(async () => {
		const from = Date.now() - Math.max(rangeMinutes, 1440) * 60_000;
		const to = Date.now();

		const [overview, operations, incidents] = await Promise.all([
			Diagnostics.getOverview({ service, rangeMinutes }),
			Diagnostics.getOperations({
				service,
				from: Date.now() - rangeMinutes * 60_000,
				to,
				limit: 40,
			}),
			Diagnostics.getIncidents({ from, to, limit: 20 }),
		]);

		return buildOverviewReport({
			service,
			rangeMinutes,
			overview,
			operations,
			incidents,
		});
	}, [rangeMinutes, service]);

	useEffect(() => {
		const startedAt = Date.now();
		setRefreshProgress(0);

		const timer = window.setInterval(() => {
			const elapsed = Date.now() - startedAt;
			setRefreshProgress(Math.min(1, elapsed / REFRESH_INTERVAL));
		}, 200);

		return () => window.clearInterval(timer);
	}, [refreshToken]);

	const secondsLeft = Math.max(
		0,
		Math.ceil((REFRESH_INTERVAL * (1 - refreshProgress)) / 1000),
	);

	return (
		<Root>
			{!hideService ? (
				<Field>
					{t("settings.systemPerformance.filters.service")}
					<Select
						style={{ width: 260 }}
						options={serviceOptions}
						value={service}
						onChange={(value) => setService(value as string)}
					/>
				</Field>
			) : null}

			<Field>
				{t("settings.systemPerformance.filters.range")}
				<Select
					style={{ width: 140 }}
					options={rangeOptions}
					value={range}
					onChange={(value) => setRange(value as RangeKey)}
				/>
			</Field>

			<RefreshBlock>
				<Button.Button
					variant="secondary"
					icon={<Icon id="refresh" size={16} />}
					title={t("settings.systemPerformance.filters.refresh") ?? ""}
					onClick={refresh}
				/>
				<ProgressTrack>
					<ProgressFill $progress={refreshProgress} />
				</ProgressTrack>
				<ProgressLabel>
					{t("settings.systemPerformance.filters.nextRefresh", {
						seconds: secondsLeft,
					})}
				</ProgressLabel>
			</RefreshBlock>

			<CopyReportButton
				labelKey="settings.systemPerformance.filters.copyReport"
				doneKey="settings.systemPerformance.filters.copyReportDone"
				errorKey="settings.systemPerformance.filters.copyReportError"
				build={buildReport}
				disabled={!service}
			/>
		</Root>
	);
};

export default Toolbar;
